import { useAddAccountOAuthResult } from "@/components/accountSwitcher/useAddAccountOAuthResult.ts";
import { providerDisplayNames } from "@/components/connectors/api.ts";
import { errorToast } from "@/components/ui/toast.tsx";
import { clearClientState } from "@/lib/clearClientState.ts";
import { stashToast } from "@/lib/deferredToast.ts";
import { api } from "@/lib/http.ts";
import { useStepUp } from "@/lib/useStepUp.ts";
import { resolveApiPath } from "@/lib/utils.ts";
import { providerStartPath } from "@/views/authProviders.ts";
import type { SwitchAccountReq, SwitchAccountResp } from "@/types/accounts.ts";
import { useMutation } from "@tanstack/react-query";
import { useCallback } from "react";

export const getProviderLabel = (provider: string) => providerDisplayNames[provider] ?? provider;

export type AddAccountProvider = "google" | "microsoft";

const formatAddAccountError = (errorCode: string): string => {
  if (errorCode === "identity_claimed_by_other_user") {
    return "That account is already connected to another Okthx account. Contact support for help.";
  }
  if (errorCode === "missing_refresh_token") {
    return "Couldn't add that account. Please try again and make sure you grant all permissions.";
  }
  if (errorCode === "scope_denied") {
    return "That account wasn't added: some of the requested permissions were declined. Try again and accept them all, or pick what to share on the permissions screen.";
  }
  return "Couldn't add that account. Please try again.";
};

/**
 * Switch-account and add-account behavior shared by every surface that lists
 * accounts (the home dropdown, Settings → Account). Owns the switch mutation,
 * the add-account OAuth popup launcher, and the popup-result listener, so the
 * two surfaces can't drift.
 *
 * `postSwitchPath` is where the browser lands after a successful switch — the
 * dropdown sends users home, Settings keeps them on the Account tab. Always a
 * bare path: assign() to it drops the current query string, which matters
 * because an account-scoped `?thread=N` would refetch a thread the new account
 * doesn't own and 403.
 */
export const useAccountActions = ({ postSwitchPath }: { postSwitchPath: string }) => {
  const switchMutation = useMutation({
    mutationFn: ({ accountId }: { accountId: number; displayName: string | null }) =>
      api.post<SwitchAccountResp, SwitchAccountReq>("/api/a/users/switch-account", { accountId }),
    onSuccess: (_data, variables) => {
      // Only name the target when we have a real identifier for it. Falling back to the
      // provider ("Google") would produce a toast like "Switched to Google" which is
      // useless when the user has multiple Google accounts.
      stashToast({
        kind: "success",
        message: variables.displayName
          ? `Switched to ${variables.displayName}.`
          : "Switched account.",
      });
      clearClientState();
      window.location.assign(postSwitchPath);
    },
    onError: () => {
      errorToast("Failed to switch account. Please try again.");
    },
  });

  const onError = useCallback((errorCode: string) => {
    // Longer duration than the default because the add-account error copy is long
    // and needs time to read + act on (contact support, etc.). Dismissible so the user
    // can clear it as soon as they've processed it.
    errorToast(formatAddAccountError(errorCode), { duration: 10_000 }, { dismissible: true });
  }, []);

  useAddAccountOAuthResult({ onError });

  const stepUp = useStepUp();
  const startAddAccount = useCallback(
    (provider: AddAccountProvider) => {
      // Adding an account waits for an account proven only by a claim link to
      // prove its number; the popup would otherwise answer OTP_REQUIRED.
      if (stepUp.required) {
        window.location.href = stepUp.href;
        return;
      }
      window.open(
        resolveApiPath(providerStartPath(provider, "add_account")),
        "_blank",
        "noopener,noreferrer",
      );
    },
    [stepUp.required, stepUp.href],
  );

  return { switchMutation, startAddAccount };
};
